/**
 * App toast facade. Re-exports Sonner's `toast` with one change: every
 * `toast.error(...)` automatically gets a green "Report on WhatsApp" action
 * that opens a 1:1 admin chat (the WHATSAPP_NUMBER direct line — NOT the
 * community group) prefilled with the error + current route, so users can
 * report any problem in one tap.
 *
 * Import `toast` from here instead of from "sonner" — all of Sonner's other
 * methods (success/info/warning/loading/promise/dismiss/…) pass through
 * untouched. Pass `{ report: false }` to `toast.error` to suppress the action
 * (e.g. pure input-validation messages where reporting to the admin is noise).
 */

import * as Sentry from "@sentry/react";
import { toast as sonnerToast, type ExternalToast } from "sonner";

import { captureEvent } from "@/lib/analyticsQueue";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import { WHATSAPP_NUMBER } from "@/config/constants";
import { translations } from "@/lib/i18n/translations";

const WHATSAPP_GREEN = "#25D366";

type ErrorMessage = Parameters<typeof sonnerToast.error>[0];
type ErrorToastData = ExternalToast & { report?: boolean };

function currentLang(): "he" | "en" {
  return localStorage.getItem("flowly-language") === "he" ? "he" : "en";
}

/** Open the admin WhatsApp chat prefilled with the failing message + route. */
function reportOnWhatsApp(message: ErrorMessage): void {
  const c = translations[currentLang()].community;
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const msgText = typeof message === "string" ? message : "";
  const prefill = `${c.reportPrefill}\n${msgText}\n(${path})`;

  captureEvent(ANALYTICS_EVENTS.SUPPORT_REPORT_CLICK, {
    source: "error_toast",
    path,
    error_message: msgText.slice(0, 200),
  });
  // A breadcrumb-grade Sentry record so a user's WhatsApp report can be
  // correlated with a trace. The error itself is captured by the existing
  // capture* helpers — this only marks that the user chose to report.
  Sentry.captureMessage("User reported an error via WhatsApp", {
    level: "info",
    tags: { surface: "error_toast" },
    extra: { toast_message: msgText, path },
  });

  window.open(
    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(prefill)}`,
    "_blank",
    "noopener,noreferrer",
  );
}

function errorWithReport(
  message: ErrorMessage,
  data?: ErrorToastData,
): string | number {
  const { report, action, ...rest } = data ?? {};

  // Caller opted out, or already supplied their own action — leave it alone.
  if (report === false || action) {
    return sonnerToast.error(message, { ...rest, ...(action ? { action } : {}) });
  }

  return sonnerToast.error(message, {
    ...rest,
    action: {
      label: translations[currentLang()].community.reportButton,
      onClick: () => reportOnWhatsApp(message),
      actionButtonStyle: { backgroundColor: WHATSAPP_GREEN, color: "#fff" },
    },
  });
}

type WrappedToast = typeof sonnerToast & {
  error: (message: ErrorMessage, data?: ErrorToastData) => string | number;
};

export const toast = Object.assign(
  (...args: Parameters<typeof sonnerToast>) => sonnerToast(...args),
  sonnerToast,
  { error: errorWithReport },
) as WrappedToast;
